import { ComponentType, ReactNode, SVGProps, useEffect, useState } from "react";
import { type Editor as TipTapEditorType } from "@tiptap/react";
import { Level } from "@tiptap/extension-heading";
import { ChevronDown, Check } from "lucide-react";
import { TextAaIcon, TextHOneIcon, TextHTwoIcon, TextHThreeIcon } from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  Tooltip,
  TooltipTrigger,
  TooltipContent
} from "@libs/zapi-ui/shadcn";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { TextSizeIcon } from "./_ToolbarIcons";

export const TOOLTIP_DELAY = 500;

export const PILL_CLASS =
  "inline-flex items-center gap-1 bg-white border border-innoveraZinc-150 rounded-full p-2 " +
  "shadow-200";

export function ToolbarButton({
  label,
  icon: Icon,
  onClick,
  isActive,
  disabled,
  children,
  "data-testid": dataTestId
}: {
  label: string;
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  children?: ReactNode;
  "data-testid"?: string;
}) {
  return (
    <Tooltip delayDuration={TOOLTIP_DELAY}>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          data-testid={dataTestId}
          disabled={disabled}
          onMouseDown={(e) => e.preventDefault()}
          onClick={onClick}
          className={cn(
            "inline-flex items-center justify-center size-8 rounded-lg text-innoveraZinc-900 transition-colors",
            "hover:bg-innoveraZinc-100 disabled:pointer-events-none disabled:text-innoveraZinc-300",
            isActive && "bg-innoveraZinc-150"
          )}
        >
          {children ?? (Icon ? <Icon className="size-4" /> : null)}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" sideOffset={8}>
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

export const Divider = () => <div className="h-5 w-px bg-innoveraZinc-150 mx-0.5" />;

export function TextStyleDropdown({ editor }: { editor: TipTapEditorType }) {
  const [states, setStates] = useState({ heading2: false, heading3: false, heading4: false });

  useEffect(() => {
    if (!editor) return;
    const update = () =>
      setStates({
        heading2: editor.isActive("heading", { level: 2 }),
        heading3: editor.isActive("heading", { level: 3 }),
        heading4: editor.isActive("heading", { level: 4 })
      });
    editor.on("selectionUpdate", update);
    editor.on("transaction", update);
    update();
    return () => {
      editor.off("selectionUpdate", update);
      editor.off("transaction", update);
    };
  }, [editor]);

  const toggleHeading = (level: Level) => editor.chain().focus().toggleHeading({ level }).run();
  const setParagraph = () => editor.chain().focus().setParagraph().run();
  const isBody = !states.heading2 && !states.heading3 && !states.heading4;

  const options = [
    { label: "Heading 1", icon: TextHOneIcon, active: states.heading2, onSelect: () => toggleHeading(2) },
    { label: "Heading 2", icon: TextHTwoIcon, active: states.heading3, onSelect: () => toggleHeading(3) },
    { label: "Heading 3", icon: TextHThreeIcon, active: states.heading4, onSelect: () => toggleHeading(4) },
    { label: "Body Text", icon: TextAaIcon, active: isBody, onSelect: setParagraph }
  ];

  return (
    <DropdownMenu>
      <Tooltip delayDuration={TOOLTIP_DELAY}>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Text style"
              data-testid="editor-text-style-trigger"
              onMouseDown={(e) => e.preventDefault()}
              className="inline-flex items-center gap-1 h-8 px-2 rounded-lg text-innoveraZinc-900 transition-colors hover:bg-innoveraZinc-100 data-[state=open]:bg-innoveraZinc-150"
            >
              <TextSizeIcon className="size-4" />
              <ChevronDown className="size-3" />
            </button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="top" sideOffset={8}>
          Text style
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="start" className="min-w-[200px] p-1">
        {options.map((option) => (
          <DropdownMenuItem
            key={option.label}
            data-testid={`editor-text-style-${option.label.toLowerCase().replace(/\s+/g, "-")}-item`}
            className="flex cursor-pointer items-center gap-2 px-2 py-1.5"
            onSelect={option.onSelect}
          >
            <option.icon className="size-4 shrink-0 text-innoveraZinc-500" />
            <span className="flex-1 text-[15px] leading-[23px] font-normal">{option.label}</span>
            {option.active && <Check className="size-4 shrink-0 text-innoveraZinc-900" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
